import Pane from 'components/Common/Pane'
import cn from 'clsx'
import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import config from 'constants/config'
import { rem } from 'polished'
import moment from 'moment'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import LastMinuteDiscountPill from './LastMinuteDiscountPill'
import WalledContent from 'components/Common/WalledContent'
import OfferBookButton from '../OfferBookButton'
import Caption from 'components/Luxkit/Typography/Caption'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import Divider from 'components/Luxkit/Divider'
import AccommodationPackageTile, { AccommodationPackageTileContent, AccommodationPackageTilePricing } from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import AccommodationPackageTilePricingBlocks from '../Accommodation/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from '../Accommodation/AccommodationPackageTilePricingBlock'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import AccommodationPackageTileInclusions from '../Accommodation/AccommodationPackageTileInclusions'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import AccommodationPackageInclusionsList from '../Accommodation/AccommodationPackageInclusionsList'
import { pluralizeToString } from 'lib/string/pluralize'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  checkIn: moment.Moment;
  duration: number;
  fetching?: boolean;
}

function LastMinutePackageOption(props: Props) {
  const {
    offer,
    packageView,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    duration,
    fetching,
  } = props

  const {
    package: pkg,
    flightPrice,
  } = packageView

  const offerPageDispatch = useOfferPageDispatch()

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (packageView.flightsAvailable || offer.bundledWithFlightsOnly) && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const onToggleFilter = useCallback((filter: OfferPageFilterTypes) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeAirport = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('airport')
  }, [onToggleFilter])

  const availableForPurchase = useIsAvailableForPurchase()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkIn && checkIn.diff(moment(), 'days') < minDay
  }, [checkIn])

  const inclusions = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusions = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (!showPrices) {
      if (packageView.hotelAvailable && !packageView.capacityValid) return 'exceeded-capacity'
      if (!packageView.hotelAvailable && checkIn) return 'sold-out-on-dates'
      if (!packageView.hotelAvailable) return 'sold-out'
    }
  }, [checkIn, packageView.capacityValid, packageView.hotelAvailable, showPrices])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('checkin')
  }, [onToggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    onToggleFilter('capacity')
  }, [onToggleFilter])

  if (!pkg.roomType) { return null }

  return (
    <Pane shadow data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile>
        <AccommodationRoomTileImage
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
          actions={availableForPurchase && packageView.capacityValid && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        >
          {availableForPurchase && <AccommodationRoomTileActions
            // @ts-expect-error not HotelPackage
            roomPackage={pkg}
            state={actionsState}
            onAddAnotherUnitClick={handleAddAnotherUnitClick}
            onChangeDatesClick={handleChangeDatesClick}
          />}
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <Divider kind="primary" />
      <AccommodationPackageTile>
        <AccommodationPackageTileContent>
          <AccommodationPackageTileDetails
            packageNumber={1}
            // @ts-expect-error not App.HotelPackage
            roomPackage={packageView.package}
          />
          <AccommodationPackageTileInclusions>
            <LargeContentToggle
              maxHeight={208}
              showMoreText="View more inclusions"
              showLessText="View less inclusions"
            >
              {bonusInclusions && <AccommodationPackageInclusionsList title="bonus">
                <MarkdownRender fontSize="medium" content={bonusInclusions} type="compact" />
              </AccommodationPackageInclusionsList>}
              <AccommodationPackageInclusionsList title={bonusInclusions ? 'additional' : undefined}>
                <MarkdownRender fontSize="medium" content={inclusions} type="compact" />
              </AccommodationPackageInclusionsList>
            </LargeContentToggle>
          </AccommodationPackageTileInclusions>
        </AccommodationPackageTileContent>
        <AccommodationPackageTilePricing>
          {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
            <AccommodationPackageTilePricingBlocks>
              {showPrices && <AccommodationPackageTilePricingBlock
                data-testid="pricing"
                title="Hotel"
                callToAction={<OfferBookButton
                  packageView={packageView}
                  data-testid={cn('HotelBookingBtn', 'buy-now-button')}
                  disabled={fetching}
                >
                  {canBuyNow ? 'Book now' : 'Check availability'}
                </OfferBookButton>}
              >
                {checkIn && <PriceRowPriceCaption>
                  {pluralizeToString('night', pkg.duration)}
                </PriceRowPriceCaption>}
                {!checkIn && <PriceRowPriceCaption>
                  {pluralizeToString('night', pkg.duration)} from
                </PriceRowPriceCaption>}
                <PriceRowPrice
                  size="M"
                  price={packageView.hotelPrice}
                  saleUnit="total"
                  taxesAndFees={packageView.taxesAndFees}
                />
                <TaxesAndFees
                  taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                  taxAmount={packageView.taxesAndFees}
                />
                <VerticalSpacer gap={4}>
                  {!!pkg?.discountPercent && <LastMinuteDiscountPill discountPercent={pkg.discountPercent} />}
                  {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />}
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={packageView.hotelPrice}
                    numberOfNights={pkg.duration}
                  />}
                </VerticalSpacer>
              </AccommodationPackageTilePricingBlock>}
              {showPrices && flightsEnabled && <AccommodationPackageTilePricingBlock
                data-testid="flights-pricing"
                title="Hotel + flights"
                callToAction={<>
                  {flightOrigin?.code !== flightDestination?.code && !departureTooEarly && <OfferBookButton
                    packageView={packageView}
                    data-buytype="flights"
                    data-testid="HotelAndFlightsBtn"
                    disabled={fetching}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>}
                  {flightOrigin?.code !== flightDestination?.code && departureTooEarly && <TextButton
                    kind="tertiary"
                    onClick={handleChangeDatesClick}
                  >
                    Change departure date
                  </TextButton>}
                  {!hasFlights && flightOrigin?.code === flightDestination?.code && <TextButton
                    kind="tertiary"
                    onClick={handleChangeAirport}
                  >
                    Change airport
                  </TextButton>}
                </>}
              >
                {!hasFlights && flightOrigin?.code === flightDestination?.code && <Caption
                  variant="large"
                  colour="neutral-three"
                >
                  No flights available from {flightOrigin?.name}. Please select another airport.
                </Caption>}
                {flightOrigin?.code !== flightDestination?.code && departureTooEarly && <Caption
                  variant="large"
                  colour="neutral-three"
                >
                  Flights cannot be booked online when check-in is within 24 hours.
                </Caption>}
                {!hasFlights && flightOrigin?.code !== flightDestination?.code && !departureTooEarly && (
                  <Caption variant="large" colour="neutral-two">
                    Add flights to the hotel package. Select from all
                    major airlines.
                  </Caption>
                )}
                {hasFlights && !departureTooEarly && <>
                  {checkIn && <PriceRowPriceCaption>
                    {pluralizeToString('night', pkg.duration)}
                  </PriceRowPriceCaption>}
                  {!checkIn && <PriceRowPriceCaption>
                    {pluralizeToString('night', pkg.duration)} from
                  </PriceRowPriceCaption>}
                  <PriceRowPrice
                    size="M"
                    price={packageView.hotelPrice}
                    saleUnit="total*"
                    taxesAndFees={packageView.taxesAndFees}
                  />
                  {!rooms && <Caption variant="medium" colour="neutral-three">
                    Two Adults
                  </Caption>}
                  <TaxesAndFees
                    taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                    taxAmount={packageView.taxesAndFees}
                  />
                  {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />}
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={packageView.hotelPrice}
                    numberOfNights={pkg.duration}
                  />}
                </>}
                <AtolProtectedTooltip />
              </AccommodationPackageTilePricingBlock>}
            </AccommodationPackageTilePricingBlocks>
            <LoadingIndicator visible={fetching} floating opaque />
          </WalledContent>}
        </AccommodationPackageTilePricing>
      </AccommodationPackageTile>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  duration: state.duration,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
}))(LastMinutePackageOption)
